import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import getAltFromSrc from 'commons/getAltFromSrc';
import JBImage from 'components/essentials/JBImage';
import Placeholder from 'components/consumer/Placeholder';
import VisuallyHidden from 'components/consumer/VisuallyHidden';
import Prefetcher from '../../../../essentials/Prefetcher/index';
import {
  getMainImageImgixPropsForPhoto,
  getThumbnailImageImgixPropsForPhoto,
} from '../helpers/imgix-props';
import usePDPDesktopHeroSizeProps from '../helpers/usePDPDesktopHeroSizeProps';
import { PDP_GALLERY_THUMBNAIL_IMAGE } from '../specs/testIds';

const PDPDesktopHeroThumbnail = ({
  children,
  cropData,
  enableCrops,
  icon,
  iconCenter = false,
  loading = false,
  isSelected = false,
  onClick = () => {},
  onLoadFailure = () => {},
  onMouseEnter = () => {},
  src,
  type = 'studio',
  isConfigFlipped = false,
  isCylindoPhoto = false,
  iconClass = '',
}) => {
  const [isError, setIsError] = useState(false);
  const mainPhotoSizeProps = usePDPDesktopHeroSizeProps();

  if (!(loading || src || children || icon) || isError) {
    return null;
  }

  return (
    <button
      className={classNames(
        'items-stretch border border-solid rounded bg-white flex justify-between h-[38px] overflow-hidden w-11 lg:max-[1100px]:h-[33px] lg:max-[1100px]:w-[39px] [&+&]:ml-1',
        'hover:cursor-pointer hover:border-brand-dark',
        'focus-visible:outline-brand',
        {
          'hover:cursor-default hover:border-gray-light8': loading,
          'border-gray-light7': !isSelected,
          'border-brand-dark': isSelected,
        }
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      type="button"
    >
      <Placeholder loading={loading} type="rect" className="!mr-0">
        <div
          className={classNames(
            'relative items-center flex justify-center h-full w-full',
            {
              'scale-x-[-1]':
                (type === 'configuration' || type === 'cylindo') &&
                isConfigFlipped,
            }
          )}
        >
          {!!src && (
            <Prefetcher
              onRenderAssets={[
                {
                  url: src,
                  imgixProps: !isCylindoPhoto
                    ? {
                        ...getMainImageImgixPropsForPhoto(
                          {
                            src,
                            type,
                            cropData,
                          },
                          enableCrops
                        ),
                        ...mainPhotoSizeProps,
                      }
                    : null,
                  isProductImage: true,
                },
              ]}
              className="block h-full !w-full"
            >
              {isCylindoPhoto ? (
                <img
                  className="h-full"
                  data-testid={PDP_GALLERY_THUMBNAIL_IMAGE}
                  src={src}
                  width={44}
                  height={38}
                  alt={getAltFromSrc(src)}
                />
              ) : (
                <JBImage
                  className={classNames('flex-1', {
                    'object-none h-[38px] w-11':
                      type === 'configuration' || type === 'cylindo',
                    'h-full object-cover w-full': type === 'studio',
                  })}
                  data-testid={PDP_GALLERY_THUMBNAIL_IMAGE}
                  forPage="pdp"
                  lazy={false}
                  onLoad={isFail => {
                    if (!isFail) {
                      return;
                    }
                    setIsError(true);
                    onLoadFailure();
                  }}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...getThumbnailImageImgixPropsForPhoto({ src, type })}
                />
              )}
            </Prefetcher>
          )}

          {!!children && (
            <div className="text-xs font-bold text-gray">{children}</div>
          )}

          {!!icon && (
            <div
              className={classNames(
                `items-center flex justify-center [&_svg]:p-[5px] ${iconClass}`,
                {
                  absolute: iconCenter,
                }
              )}
            >
              {icon}
            </div>
          )}
        </div>
      </Placeholder>

      {!!isSelected && <VisuallyHidden>- selected</VisuallyHidden>}
    </button>
  );
};

PDPDesktopHeroThumbnail.propTypes = {
  children: PropTypes.node,
  cropData: PropTypes.object,
  enableCrops: PropTypes.bool,
  icon: PropTypes.node,
  iconCenter: PropTypes.bool,
  iconClass: PropTypes.string,
  isConfigFlipped: PropTypes.bool,
  isCylindoPhoto: PropTypes.bool,
  isSelected: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  onLoadFailure: PropTypes.func,
  onMouseEnter: PropTypes.func,
  src: PropTypes.string,
  type: PropTypes.oneOf([
    'buildyourown',
    'configuration',
    'cylindo',
    'dimensional',
    'studio',
  ]),
};

export default PDPDesktopHeroThumbnail;
